import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { trendingVideosAPI, mediaAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Save, Upload, Loader2, Play, Trash2, Link, Video } from 'lucide-react';
import { getImageUrl } from '../../lib/utils';

export const AdminTrendingVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [uploading, setUploading] = useState({});
  const [videoUploading, setVideoUploading] = useState({});

  // Initialize 5 slots
  const initializeSlots = (existingVideos) => {
    const slots = [];
    for (let i = 1; i <= 5; i++) {
      const existing = existingVideos.find(v => v.slot_order === i);
      slots.push(existing || {
        id: null,
        title: '',
        description: '',
        video_url: '',
        video_file: '',
        video_type: 'url', // 'url' or 'upload'
        thumbnail: '',
        credit: '',
        duration: '',
        slot_order: i,
        is_active: false
      });
    }
    return slots;
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await trendingVideosAPI.getAllAdmin();
      setVideos(initializeSlots(res.data));
    } catch (error) {
      console.error('Error fetching videos:', error);
      setVideos(initializeSlots([]));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (slotIndex, field, value) => {
    setVideos(prev => prev.map((v, i) => 
      i === slotIndex ? { ...v, [field]: value } : v
    ));
  };

  const handleThumbnailUpload = async (slotIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [slotIndex]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await mediaAPI.upload(formData);
      handleChange(slotIndex, 'thumbnail', res.data.url);
      toast.success('Thumbnail uploaded');
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploading(prev => ({ ...prev, [slotIndex]: false }));
    }
  };

  const handleVideoUpload = async (slotIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video file must be less than 100MB');
      return;
    }

    setVideoUploading(prev => ({ ...prev, [slotIndex]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await mediaAPI.upload(formData);
      handleChange(slotIndex, 'video_file', res.data.url);
      handleChange(slotIndex, 'video_type', 'upload');
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setVideoUploading(prev => ({ ...prev, [slotIndex]: false }));
    }
  };

  const handleSave = async (slotIndex) => {
    const video = videos[slotIndex];
    
    if (!video.title) {
      toast.error('Title is required');
      return;
    }

    setSaving(prev => ({ ...prev, [slotIndex]: true }));
    try {
      await trendingVideosAPI.updateBySlot(video.slot_order, {
        title: video.title,
        description: video.description,
        video_url: video.video_type === 'url' ? video.video_url : '',
        video_file: video.video_type === 'upload' ? video.video_file : '',
        video_type: video.video_type,
        thumbnail: video.thumbnail,
        credit: video.credit,
        duration: video.duration,
        is_active: video.is_active
      });
      toast.success(`Slot ${video.slot_order} saved`);
      fetchVideos();
    } catch (error) {
      toast.error('Failed to save video');
    } finally {
      setSaving(prev => ({ ...prev, [slotIndex]: false }));
    }
  };

  const handleDelete = async (slotIndex) => {
    const video = videos[slotIndex];
    if (!video.id) return;

    try {
      await trendingVideosAPI.delete(video.id);
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-trending-videos-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold">The Trade Shorts</h1>
          <p className="text-muted-foreground">Manage the 5 short video slots on the homepage. Upload videos directly or link to YouTube/Instagram.</p>
        </div>

        {/* Video Slots */}
        {loading ? (
          <div className="space-y-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {videos.map((video, index) => (
              <div 
                key={video.slot_order} 
                className="border border-border rounded-sm p-6"
                data-testid={`video-slot-${video.slot_order}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-lg font-bold">
                    Slot {video.slot_order}
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${index}`} className="text-sm">Active</Label>
                      <Switch
                        id={`active-${index}`}
                        checked={video.is_active}
                        onCheckedChange={(checked) => handleChange(index, 'is_active', checked)}
                      />
                    </div>
                    {video.id && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={video.title}
                        onChange={(e) => handleChange(index, 'title', e.target.value)}
                        placeholder="Video title"
                        data-testid={`video-title-${video.slot_order}`}
                      />
                    </div>

                    {/* Video Source Type Toggle */}
                    <div className="space-y-3">
                      <Label>Video Source</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={video.video_type === 'url' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleChange(index, 'video_type', 'url')}
                          className="flex-1"
                        >
                          <Link className="h-4 w-4 mr-2" />
                          YouTube/Link
                        </Button>
                        <Button
                          type="button"
                          variant={video.video_type === 'upload' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleChange(index, 'video_type', 'upload')}
                          className="flex-1"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Video
                        </Button>
                      </div>
                    </div>

                    {/* URL Input or Upload */}
                    {video.video_type === 'url' ? (
                      <div className="space-y-2">
                        <Label>Video URL</Label>
                        <Input
                          value={video.video_url}
                          onChange={(e) => handleChange(index, 'video_url', e.target.value)}
                          placeholder="https://youtube.com/shorts/... or https://instagram.com/reel/..."
                          data-testid={`video-url-${video.slot_order}`}
                        />
                        <p className="text-xs text-muted-foreground">
                          Supports: YouTube, YouTube Shorts, Instagram Reels, TikTok
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>Upload Video File</Label>
                        {video.video_file ? (
                          <div className="space-y-2">
                            <div className="p-3 bg-secondary/50 rounded-sm flex items-center gap-2">
                              <Video className="h-4 w-4 text-primary" />
                              <span className="text-sm truncate flex-1">{video.video_file.split('/').pop()}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleChange(index, 'video_file', '')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <video 
                              src={getImageUrl(video.video_file)} 
                              className="w-full aspect-[9/16] max-h-48 object-cover rounded-sm bg-black"
                              controls
                            />
                          </div>
                        ) : (
                          <label className="cursor-pointer block">
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/quicktime"
                              onChange={(e) => handleVideoUpload(index, e)}
                              className="hidden"
                            />
                            <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-primary/50 transition-colors">
                              {videoUploading[index] ? (
                                <div className="flex flex-col items-center gap-2">
                                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                  <p className="text-sm text-muted-foreground">Uploading video...</p>
                                </div>
                              ) : (
                                <div className="flex flex-col items-center gap-2">
                                  <Upload className="h-8 w-8 text-muted-foreground" />
                                  <p className="text-sm font-medium">Click to upload video</p>
                                  <p className="text-xs text-muted-foreground">MP4, WebM, MOV (max 100MB)</p>
                                </div>
                              )}
                            </div>
                          </label>
                        )}
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Credit/Source</Label>
                        <Input
                          value={video.credit}
                          onChange={(e) => handleChange(index, 'credit', e.target.value)}
                          placeholder="Source name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Duration</Label>
                        <Input
                          value={video.duration}
                          onChange={(e) => handleChange(index, 'duration', e.target.value)}
                          placeholder="0:45"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={video.description}
                        onChange={(e) => handleChange(index, 'description', e.target.value)}
                        placeholder="Optional description"
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Right Column - Thumbnail */}
                  <div className="space-y-4">
                    <Label>Thumbnail</Label>
                    {video.thumbnail ? (
                      <div className="relative aspect-[9/16] max-h-64 bg-muted rounded-sm overflow-hidden">
                        <img
                          src={getImageUrl(video.thumbnail)}
                          alt="Thumbnail"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-12 w-12 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-[9/16] max-h-64 bg-muted rounded-sm flex items-center justify-center">
                        <p className="text-muted-foreground text-sm">No thumbnail</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Input
                        value={video.thumbnail}
                        onChange={(e) => handleChange(index, 'thumbnail', e.target.value)}
                        placeholder="Thumbnail URL"
                        className="flex-1"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleThumbnailUpload(index, e)}
                          className="hidden"
                        />
                        <Button type="button" variant="outline" size="icon" disabled={uploading[index]} asChild>
                          <span>
                            {uploading[index] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="mt-4 pt-4 border-t border-border flex justify-end">
                  <Button 
                    onClick={() => handleSave(index)}
                    disabled={saving[index]}
                    data-testid={`save-slot-${video.slot_order}`}
                  >
                    {saving[index] ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Slot {video.slot_order}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTrendingVideos;
